import {RecodeEvents} from '@fantasmagames/slot/src/integration/RecodeEvents';
import {RecodeLogic} from '@fantasmagames/slot/src/logic/integration/RecodeLogic';
import {AutoplayHandlerEvents} from '@fantasmagames/slot/src/modules/autoplay/AutoplayHandlerEvents';
import {AutoplayHandlerModule} from '@fantasmagames/slot/src/modules/autoplay/AutoplayHandlerModule';
import {FreeRoundsScreenEvents} from '@fantasmagames/slot/src/modules/microgaming/freerounds/FreeRoundsScreenEvents';
import {ReelsEvents} from '@fantasmagames/slot/src/modules/reels/spin/ReelsEvents';
import {RecodeGameAutoplayHandlerModuleEvents} from '../recode/RecodeGameAutoplayHandlerModuleEvents';
import {AutoplayBasicState} from '@fantasmagames/slot/src/modules/autoplay/states/AutoplayBasicState';
import {RecodeAutoplayBasicState} from '@fantasmagames/slot/src/modules/autoplay/recode/states/RecodeAutoplayBasicState';

export interface IAutoplayOptions {
	spins: string | number;
	selectedCoinSize: number;
	coinSizeOptions: number[];
	selectedBetSize: number;
	selectedCoinsBet: number;
	betSizeOptions: number[];
	numPaylines: number;
	isJackpotAvailable?: boolean;
	winLimit?: number;
	lossLimit?: number;
	stopOnJackpotWin?: boolean;
	sideBet?: number;
}

export interface IVerifyWagerLimitsParameters {
	/**
	 * The next bet amount being wagered by the player.
	 */
	nextBetAmount: number;
	/**
	 * The amount won by the player in the previous AutoPlay wager.
	 */
	previousWinAmount: number;
	/**
	 * Indicates whether the next wager is due to a triggered effect.
	 * If set to true, the AutoPlay wager count is not incremented.
	 */
	isTriggeredEffect: boolean;
}

export interface IVerifyWagerLimitsResult {
	/**
	 * A message that describes the reason for not allowing a wager.
	 */
	messageText: string;
	/**
	 * Indicates whether the next wager would be allowed based on
	 * current limits.
	 */
	autoPlayLimitReached: boolean;
	/**
	 * The type of limit which is stopping AutoPlay:
	 */
	limit: IAutoPlayWagerLimitReason;
}

export enum IAutoPlayWagerLimitReason {
	/**
	 * No limits have been exceeded.
	 */
	none = 'none',
	/**
	 * Spin count limit reached
	 */
	spinCount = 'spinCount',
	/**
	 * Loss limit reached
	 */
	lossLimit = 'lossLimit',
	/**
	 * Win limit reached
	 */
	winLimit = 'winLimit',
}

export class RecodeGameAutoplayHandlerModule extends AutoplayHandlerModule {
	protected _useWagerLimitAPI: boolean;
	protected _winAmount: number;

	protected _isEnabled: boolean = false;

	protected _currentVerifyWagerAPIResults: IVerifyWagerLimitsResult;

	protected setupStates(): void {
		super.setupStates();

		this.addState(new RecodeAutoplayBasicState(this, AutoplayHandlerModule.BASIC));
	}

	protected setupEvents(): void {
		super.setupEvents();

		this.on(RecodeEvents.SHOWING_ERROR, this.onShowingErrorDialog);
		this.on(RecodeEvents.SERVER_AUTOPLAY_INTERRUPT, this.onServerInterruptAutoplay);

		this.on(FreeRoundsScreenEvents.FREE_ROUNDS_SCREEN_SHOWN, this.onFreeRoundsScreenShow);

		this.on(ReelsEvents.SPIN_ENDED, this.onSpinEnded);

		this._useWagerLimitAPI = (window as any).V.API.Autoplay.getConfig().useWagerLimitApi;
	}

	protected onFreeRoundsScreenShow(): void {
		this.stopAutoplayAndEmitRoundsFinished();
	}

	protected getLogic(): RecodeLogic {
		return this.logic as RecodeLogic;
	}

	public initVAPIAutoplay(): void {
		const options: IAutoplayOptions = {
			spins: this._autospinsLeft,
			selectedCoinSize: this.getLogic().chipSize,
			coinSizeOptions: this.getLogic().validChips,
			selectedBetSize: this.getLogic().betValue,
			selectedCoinsBet: this.getLogic().numChips,
			betSizeOptions: this.getLogic().betValues,
			numPaylines: this.getLogic().paylines,
		};

		if (this.logic.autoplayData.winLimitEnabled) {
			options.winLimit = this._winLimit || 0;
		}

		if (this.logic.autoplayData.lossLimitEnabled) {
			options.lossLimit = this._lossLimit || 0;
		}

		const startCallback = () => {
			// This should only be used if we don't use our own implementation of the Autoplay API from V.
			// console.warn('Autoplay has started.');
		};
		const cancelCallback = () => {
			// This should only be used if we don't use our own implementation of the Autoplay API from V.
			// console.warn('Autoplay menu has been canceled.');
		};

		const context = this;

		(window as any).V.API.Autoplay.init(options, startCallback, cancelCallback, context);
	}

	/**
	 * Verifies the wager limits and current values using V WagerLimitAPI. Sets the result to member variable currentVerifyWagerResults
	 */
	protected verifyWagerLimits(): void {
		const verifyWagerParameters: IVerifyWagerLimitsParameters = this.wagerLimitParameters;

		this._currentVerifyWagerAPIResults = (window as any).V.API.Autoplay.verifyWagerLimits(verifyWagerParameters);
	}

	/**
	 * Checks if autoplay should stop on wagerlimits.
	 *
	 * @protected
	 * @returns {boolean}
	 * @memberof RecodeGameAutoplayHandlerModule
	 */
	protected shouldStopOnWagerLimits(): boolean {
		if (this._useWagerLimitAPI) {
			this.verifyWagerLimits();

			return this.currentVerifyWagerAPIResults.autoPlayLimitReached;
		}

		return false;
	}

	protected stopOnWagerLimits(): void {
		this.stopAutoplayAndEmitRoundsFinished();
		this.sendAutoplayMessage(this.currentVerifyWagerAPIResults.messageText, this.currentVerifyWagerAPIResults.limit);
	}

	protected isLastFreeGame(): boolean {
		const logic: RecodeLogic = this.logic as RecodeLogic;
		if (logic.currentFreeGameProgress && logic.currentFreeGameProgress.gamesRemaining === 0) {
			return true;
		}

		return false;
	}

	public tryToStartAutoplay(): void {
		if (this.isFreeSpinsStarted()) {
			throw new Error('RecodeGameAutoplayHandlerModule::tryToStartAutoplay impossible condition');
		}

		if (this.shouldStopOnWagerLimits()) {
			this.stopOnWagerLimits();

			return;
		}

		if (this.isLastFreeGame()) {
			return;
		}

		this.logic.communicator.api_autoPlayStarted();
		super.tryToStartAutoplay();
	}

	/**
	 * this is really shitty fix, but it's hard to find another
	 */
	protected forceStartAutoplay(): void {
		if (this.shouldStopOnWagerLimits()) {
			this.stopOnWagerLimits();

			return;
		}

		this.logic.communicator.api_autoPlayStarted();
		super.tryToStartAutoplay();
	}

	public stopAutoplay(): void {
		if (this._isEnabled) {
			this.timeUtils.killTimer(this._currentTimer);
			this.eventManager.emit(AutoplayHandlerEvents.AUTOPLAY_STOPPED);
			this._isEnabled = false;
			this.logic.communicator.api_autoPlayStopped();
		}
	}

	protected onSpinEnded(): void {
		if (this._isEnabled) {
			this._totalAutoplayWin += this.logic.getWin();
			this._winAmount = this.logic.getWin();
		}
	}

	protected onBigWin(): void {}

	protected onWin(): void {}

	// Recieves the autoplayInterrupt struct
	// If the interrupt message (currently: args[1][0][1]) isn't undefined send message to info popup.
	protected onServerInterruptAutoplay(...args: any[]): void {
		this._isEnabled = true;
		this.stopAutoplay();

		if (!!args[1][0][1]) {
			this.sendAutoplayMessage(args[1][0][1]);
		}

		this.onAutoplayValuesUpdated(null, {
			singleWinLimit: 0,
			winLimit: 0,
			lossLimit: 0,
			stopAutoplayOnWin: false,
		});
		this.eventManager.emit(AutoplayHandlerEvents.AUTOPLAY_ROUNDS_FINISHED);
	}

	protected onShowingErrorDialog(): void {
		this.stopAutoplay();
	}

	protected sendAutoplayMessage(message: string, reason?: string): void {
		this.eventManager.emit(RecodeGameAutoplayHandlerModuleEvents.RECODE_AUTOPLAY_SHOW_POPUP, message, reason);
	}

	public winLimitStartCheck(): boolean {
		return this._stopOnWinLimit && this.logic.autoplayData.winLimitsAmounts.indexOf(this._winLimit) === -1;
	}

	public lossLimitStartCheck(): boolean {
		return this._stopOnLossLimit && this.logic.autoplayData.lossLimitsAmounts.indexOf(this._lossLimit) === -1;
	}

	protected get currentVerifyWagerAPIResults(): IVerifyWagerLimitsResult {
		return this._currentVerifyWagerAPIResults;
	}

	protected get wagerLimitParameters(): IVerifyWagerLimitsParameters {
		return {nextBetAmount: this.logic.betValue, previousWinAmount: this._winAmount, isTriggeredEffect: false};
	}

	public get useWagerLimitAPI(): boolean {
		return this._useWagerLimitAPI;
	}
}



// WEBPACK FOOTER //
// ./@fantasmagames/slot/src/modules/autoplay/recode/RecodeGameAutoplayHandlerModule.ts